import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  TextField,
  Grid,
  Divider,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  RadioGroup,
  Radio,
  FormControlLabel,
  IconButton,
  OutlinedInput,
  Checkbox,
  Tooltip,
  makeStyles
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import PhotoCameraIcon from '@material-ui/icons/PhotoCamera';
import DeleteIcon from '@material-ui/icons/Delete';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import { useDispatch } from 'react-redux';
import { toPng } from 'html-to-image';
import { saveAs } from 'file-saver';
import moment from 'moment';
import { messageToastType_const } from 'src/app/constant/config';
import { showGlobalToast } from 'src/features/uiSlice';
import { getListAgencies } from 'src/features/agencySlice';
import { getListUsers } from 'src/features/userSlice';
import axiosInterceptor from 'src/app/axiosInterceptor';

const useStyles = makeStyles(theme => ({
  customTooltip: {
    backgroundColor: '#262930',
    color: '#ffffff',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.45)',
    borderRadius: 10,
    padding: '12px 16px',
    maxWidth: 480,
    maxHeight: 'calc(100vh - 80px)',
    overflowY: 'auto',
    border: '1px solid #3a3e47',
    '&::-webkit-scrollbar': {
      width: '6px'
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#ff7b92',
      borderRadius: '4px'
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: '#1c1f26',
      borderRadius: '4px'
    }
  },
  customArrow: {
    color: '#262930'
  }
}));

const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) {
    return url;
  }
  const imgServer = process.env.REACT_APP_IMG_SERVER || process.env.REACT_APP_BACKEND_URL || '';
  const cleanServer = imgServer.replace(/\/+$/, '');
  const cleanUrl = url.replace(/^\/+/, '');
  return `${cleanServer}/${cleanUrl}`;
};

const getProductImageUrl = (p) => {
  if (!p) return '';
  let rawUrl = '';
  let images = p.images;
  if (typeof images === 'string') {
    try {
      images = JSON.parse(images);
    } catch (e) {
      images = null;
    }
  }
  if (Array.isArray(images) && images.length > 0) {
    const primary = images.find(i => i && (i.is_primary || i.isPrimary)) || images[0];
    rawUrl = primary?.url || (typeof primary === 'string' ? primary : '');
  }
  if (!rawUrl) {
    rawUrl = p.image || p.image_url || p.imageUrl || p.url || '';
  }
  return getImageUrl(rawUrl);
};

const OrderDialog = ({
  open,
  onClose,
  isEditing,
  editOrderId,
  submitting,
  form,
  setForm,
  devices = [],
  setDevices,
  orderParts = [],
  setOrderParts,
  onSaveSuccess,
  tabForm,
  setTabForm,
  fetchType,
  setFetchType,
  suggestionLoading,
  setSuggestionLoading,
  selectedUser,
  setSelectedUser,
  setSelectedImages,
  handleCreateOrder,
  handleFetchUserInfo,
  handleAutocompleteSearch,
  handleAddDevice,
  handleRemoveDevice,
  handleDeviceChange,
  listDeviceType,
  listAgencies,
  listUsers,
  listSales,
  isAdmin,
  isAdminSale,
  isAgencySale,
  dataLogin,
  formatMoney,
  formatInputMoney,
  parseInputMoney
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const captureRef = useRef(null);

  const isLeadMarketing = Boolean(dataLogin?.role_id === 11 || dataLogin?.role?.title === 'lead_marketing');
  const canSelectAgency = isAdmin || isAdminSale;

  useEffect(() => {
    if (open && !canSelectAgency && tabForm !== 1) {
      setTabForm(1);
      setFetchType('phone');
    }
  }, [open, canSelectAgency]);

  const isDeviceChecked = form.order_type === 'SALE' || form.order_type === 'BOTH';
  const isPartChecked = form.order_type === 'PART_SALE' || form.order_type === 'BOTH';

  const is2CamDevice = (devices || []).some(d => {
    const t = (d.type || '').toUpperCase().replace('-', '_');
    return t === 'VGPS_2CAM' || t.includes('2CAM');
  });

  useEffect(() => {
    if (!form.simNetwork) return;
    if (is2CamDevice) {
      if (['Viettel', 'Vinaphone'].includes(form.simNetwork)) {
        setForm(prev => ({ ...prev, simNetwork: '' }));
      }
    } else {
      if (['Sim ảnh', 'Sim live'].includes(form.simNetwork)) {
        setForm(prev => ({ ...prev, simNetwork: '' }));
      }
    }
  }, [is2CamDevice]);

  const handleOrderTypeCheckboxChange = (type, checked) => {
    let nextOrderType = form.order_type || 'SALE';
    if (type === 'device') {
      if (checked) {
        nextOrderType = isPartChecked ? 'BOTH' : 'SALE';
        if (devices.length === 0) {
          setDevices([{ type: 'VGPS_CAM4G', quantity: 1, unitPrice: 0 }]);
        }
      } else {
        if (isPartChecked) {
          nextOrderType = 'PART_SALE';
        }
      }
    } else if (type === 'part') {
      if (checked) {
        nextOrderType = isDeviceChecked ? 'BOTH' : 'PART_SALE';
        if (orderParts.length === 0) {
          setOrderParts([
            { product_id: '', product_code: '', product_name: '', quantity: 1, unit_price: 0, discount: 0, agency_id: '', description: '' }
          ]);
        }
      } else {
        if (isDeviceChecked) {
          nextOrderType = 'SALE';
        }
      }
    }
    setForm(prev => ({ ...prev, order_type: nextOrderType }));
  };

  const [productsList, setProductsList] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);

  useEffect(() => {
    if (open) {
      setLoadingProducts(true);
      axiosInterceptor.get('/product?page=1&page_size=1000')
        .then(res => {
          const list = res.data?.payload?.data?.products
            || res.data?.payload?.products
            || (Array.isArray(res.data?.payload?.data) ? res.data?.payload?.data : [])
            || [];
          setProductsList(list);
        })
        .catch(err => console.error('Failed to load products list:', err))
        .finally(() => setLoadingProducts(false));
    }
  }, [open]);

  const handleAddPart = () => {
    setOrderParts([
      ...orderParts,
      { product_id: '', product_code: '', product_name: '', quantity: 1, unit_price: 0, discount: 0, agency_id: '', description: '' }
    ]);
  };

  const handleRemovePart = index => {
    setOrderParts(orderParts.filter((_, i) => i !== index));
  };

  const handlePartChange = (index, field, value) => {
    const newParts = [...orderParts];
    newParts[index] = {
      ...newParts[index],
      [field]: value
    };
    setOrderParts(newParts);
  };

  const handlePartSelect = (index, newValue) => {
    const newParts = [...orderParts];
    newParts[index] = {
      ...newParts[index],
      product_id: newValue ? newValue.id : '',
      product_code: newValue ? newValue.code : '',
      product_name: newValue ? newValue.name : ''
    };
    setOrderParts(newParts);
  };

  const handleCaptureScreenshot = async () => {
    if (!captureRef.current) {
      dispatch(
        showGlobalToast({
          message: 'Không tìm thấy vùng cần chụp!',
          type: messageToastType_const.warning
        })
      );
      return;
    }
    dispatch(
      showGlobalToast({
        message: 'Đang xử lý chụp màn hình, vui lòng đợi...',
        type: messageToastType_const.info
      })
    );
    try {
      const element = captureRef.current;

      const dataUrl = await toPng(element, {
        cacheBust: true,
        backgroundColor: '#ffffff',
        pixelRatio: 2,
        filter: node => {
          if (node.classList && node.classList.contains('no-capture')) {
            return false;
          }
          return true;
        },
        style: {
          padding: '24px',
          borderRadius: '0px'
        }
      });

      const filename = `don_hang_${form.fullName ||
        form.account ||
        'moi'}_${moment().format('YYYYMMDD_HHmmss')}.png`;
      saveAs(dataUrl, filename);

      dispatch(
        showGlobalToast({
          message: 'Đã lưu ảnh phiếu bán hàng!',
          type: messageToastType_const.success
        })
      );
    } catch (error) {
      console.error('Capture screenshot error:', error);
      dispatch(
        showGlobalToast({
          message: 'Lỗi khi chụp màn hình: ' + error.message,
          type: messageToastType_const.error
        })
      );
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle disableTypography>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" style={{ fontWeight: 700, color: '#1a1f36' }}>
            {isEditing ? `Sửa phiếu: ${editOrderId}` : '+ Thông tin đơn hàng'}
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            startIcon={<PhotoCameraIcon />}
            onClick={handleCaptureScreenshot}
            style={{ fontWeight: 600, textTransform: 'none' }}
          >
            Chụp ảnh phiếu
          </Button>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Box pt={1} pb={1} ref={captureRef} style={{ backgroundColor: '#fff' }}>
          <Tabs
            value={canSelectAgency ? tabForm : 1}
            onChange={(_, v) => {
              if (!canSelectAgency && v === 0) return;
              setTabForm(v);
              setSelectedUser(null);
              setForm(prev => ({
                ...prev,
                account: '',
                id: null,
                fullName: '',
                phone: '',
                company: '',
                account_name: '',
                type: '',
                simType: '',
                simNetwork: '',
                paymentMethod: 'Tiền mặt',
                isNewOrder: '',
                installationType: ''
              }));
              setDevices(prevDevices =>
                (prevDevices || []).map(d => ({
                  ...d,
                  simType: v === 1 ? 'Sim VGPS' : (d.simType || 'Sim đại lý'),
                  simNetwork: v === 1 && d.simType === 'Sim đại lý' ? '' : d.simNetwork
                }))
              );
              setSelectedImages([]);
              setFetchType(v === 0 ? 'account' : 'phone');
              // Không cần gọi API ở đây vì đã có handleAutocompleteSearch khi gõ
            }}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
            style={{ marginBottom: 20, borderBottom: '1px solid #eee' }}
          >
            {canSelectAgency && (
              <Tab
                value={0}
                label="Đại lý"
                disabled={isEditing}
                style={{ fontWeight: 600, textTransform: 'none' }}
              />
            )}
            <Tab
              value={1}
              label="Khách lẻ"
              disabled={isEditing}
              style={{ fontWeight: 600, textTransform: 'none' }}
            />
          </Tabs>

          <Box
            display="flex"
            flexDirection="column"
            style={{ marginBottom: 16 }}
          >
            <Box display="flex" style={{ gap: 8, marginBottom: 8 }}>
              {tabForm === 0 && (
                <FormControl
                  variant="outlined"
                  size="small"
                  style={{ minWidth: 140 }}
                >
                  <Select
                    value={fetchType}
                    disabled={isEditing}
                    onChange={e => setFetchType(e.target.value)}
                  >
                    <MenuItem value="account">Tài khoản</MenuItem>
                    <MenuItem value="phone">Số điện thoại</MenuItem>
                  </Select>
                </FormControl>
              )}
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel
                  variant="outlined"
                  shrink
                  style={{
                    backgroundColor: 'white',
                    padding: '0 4px',
                    marginLeft: -4
                  }}
                >
                  {tabForm === 0
                    ? fetchType === 'account'
                      ? isEditing ? 'Tài khoản khách hàng' : 'Tìm tài khoản khách hàng'
                      : isEditing ? 'Số điện thoại khách hàng' : 'Tìm số điện thoại khách hàng'
                    : isEditing ? 'Số điện thoại khách hàng' : 'Tìm số điện thoại khách hàng'}
                </InputLabel>
                <Autocomplete
                  fullWidth
                  disabled={isEditing}
                  size="small"
                  openOnFocus={tabForm === 0}
                  options={tabForm === 0 ? listAgencies || [] : listUsers || []}
                  loading={suggestionLoading}
                  filterOptions={x => x}
                  value={selectedUser}
                  isOptionEqualToValue={(option, value) =>
                    (option.id || option.agency_id || option.user_id) ===
                    (value.id || value.agency_id || value.user_id)
                  }
                  getOptionLabel={option => {
                    if (!option) return '';
                    if (tabForm === 0) {
                      return fetchType === 'account'
                        ? option.username || option.name || ''
                        : option.leader?.phone || option.phone || '';
                    }
                    return (
                      option.phone || option.username || option.full_name || ''
                    );
                  }}
                  onInputChange={(event, newInputValue) => {
                    setForm(f => ({ ...f, account: newInputValue }));
                    handleAutocompleteSearch(newInputValue);
                    if (!newInputValue) setSelectedUser(null);
                  }}
                  onChange={(event, newValue) => {
                    setSelectedUser(newValue);
                    handleFetchUserInfo(newValue);
                  }}
                  noOptionsText={
                    suggestionLoading
                      ? 'Đang tìm kiếm...'
                      : 'Không tìm thấy kết quả'
                  }
                  renderOption={option => (
                    <Box>
                      <Typography style={{ fontWeight: 600, fontSize: 13 }}>
                        {tabForm === 0
                          ? option.name
                          : option.full_name || option.fullname}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {tabForm === 0
                          ? `${option.username || ''}${option.username ? ' - ' : ''
                          }${option.leader?.phone || option.phone || ''}`
                          : `${option.username || ''}${option.username ? ' - ' : ''
                          }${option.phone || ''}`}
                      </Typography>
                    </Box>
                  )}
                  renderInput={params => (
                    <TextField
                      {...params}
                      label={
                        tabForm === 0
                          ? fetchType === 'account'
                            ? isEditing ? 'Tài khoản khách hàng' : 'Tìm tài khoản khách hàng'
                            : isEditing ? 'Số điện thoại khách hàng' : 'Tìm số điện thoại khách hàng'
                          : isEditing ? 'Số điện thoại khách hàng' : 'Tìm số điện thoại khách hàng'
                      }
                      variant="outlined"
                      InputLabelProps={{
                        ...params.InputLabelProps,
                        style: { display: 'none' }
                      }}
                    />
                  )}
                />
              </FormControl>
            </Box>
          </Box>

          <Grid container spacing={2} style={{ marginBottom: 16 }}>
            <Grid item xs={12} sm={6}>
              <Typography
                variant="subtitle2"
                style={{ fontSize: 13, color: '#555', marginBottom: 4 }}
              >
                {tabForm === 0 ? 'Tên đại lý/công ty' : 'Tên khách hàng'}
              </Typography>
              <TextField
                fullWidth
                disabled
                value={form.fullName}
                variant="outlined"
                size="small"
                InputProps={{ notched: false }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography
                variant="subtitle2"
                style={{ fontSize: 13, color: '#555', marginBottom: 4 }}
              >
                {tabForm === 0 && fetchType === 'phone'
                  ? 'Tài khoản'
                  : 'Số điện thoại'}
              </Typography>
              <TextField
                fullWidth
                disabled
                value={
                  tabForm === 0 && fetchType === 'phone'
                    ? form.account_name
                    : form.phone
                }
                variant="outlined"
                size="small"
                InputProps={{ notched: false }}
              />
            </Grid>
          </Grid>

          <Box mb={2}>
            <Typography
              variant="subtitle2"
              style={{ fontSize: 13, color: '#555', marginBottom: 4 }}
            >
              Địa chỉ
            </Typography>
            <TextField
              fullWidth
              disabled
              value={form.company}
              variant="outlined"
              size="small"
              InputProps={{ notched: false }}
            />
          </Box>

          {isLeadMarketing && (
            <Box mb={2}>
              <Typography
                variant="subtitle2"
                style={{ fontSize: 13, color: '#555', marginBottom: 4 }}
              >
                Nhân viên Sale phụ trách
              </Typography>
              <FormControl fullWidth variant="outlined" size="small">
                <Select
                  value={form.sale_id || dataLogin?.id || ''}
                  onChange={e => setForm({ ...form, sale_id: e.target.value })}
                >
                  <MenuItem value={dataLogin?.id}>
                    Chính tôi ({dataLogin?.full_name || dataLogin?.username || 'Lead'})
                  </MenuItem>
                  {(listSales || [])
                    .filter(s => s.manager_id === dataLogin?.id && s.id !== dataLogin?.id)
                    .map(subSale => (
                      <MenuItem key={subSale.id} value={subSale.id}>
                        {subSale.full_name || subSale.username || subSale.phone}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Box>
          )}

          <Box mb={2}>
            <Typography
              variant="subtitle2"
              style={{ fontSize: 13, color: '#555', marginBottom: 4 }}
            >
              Hình thức thanh toán
            </Typography>
            <FormControl fullWidth variant="outlined" size="small">
              <Select
                name="paymentMethod"
                value={form.paymentMethod || 'Tiền mặt'}
                onChange={e =>
                  setForm({ ...form, paymentMethod: e.target.value })
                }
              >
                <MenuItem value="Tiền mặt">Tiền mặt</MenuItem>
                <MenuItem value="Chuyển khoản">Chuyển khoản</MenuItem>
                {tabForm === 0 && <MenuItem value="Công nợ">Công nợ</MenuItem>}
                <MenuItem value="COD">COD</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box mb={2}>
            <FormControl component="fieldset">
              <Typography
                variant="subtitle2"
                style={{ fontSize: 13, color: '#555', marginBottom: 4 }}
              >
                Loại đơn hàng <span style={{ color: 'red' }}>*</span>
              </Typography>
              <Box display="flex" style={{ gap: 16 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isDeviceChecked}
                      onChange={e => handleOrderTypeCheckboxChange('device', e.target.checked)}
                      color="primary"
                      size="small"
                    />
                  }
                  label={
                    <Typography style={{ fontSize: 13, fontWeight: 600 }}>
                      Đơn bán thiết bị VGPS
                    </Typography>
                  }
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isPartChecked}
                      onChange={e => handleOrderTypeCheckboxChange('part', e.target.checked)}
                      color="primary"
                      size="small"
                    />
                  }
                  label={
                    <Typography style={{ fontSize: 13, fontWeight: 600 }}>
                      Đơn bán phụ kiện
                    </Typography>
                  }
                />
              </Box>
            </FormControl>
          </Box>

          {form.order_type !== 'PART_SALE' && (
            <>
              {tabForm === 0 ? (
                <Grid container spacing={2} style={{ marginBottom: 16 }}>
                  <Grid item xs={12}>
                    <FormControl component="fieldset">
                      <Typography
                        variant="subtitle2"
                        style={{ fontSize: 13, color: '#666', marginBottom: 4 }}
                      >
                        Hình thức kích hoạt <span style={{ color: 'red' }}>*</span>
                      </Typography>
                      <RadioGroup
                        row
                        value={form.type}
                        onChange={e => setForm({ ...form, type: e.target.value })}
                      >
                        <FormControlLabel
                          value="đại lý kích hoạt"
                          control={<Radio color="primary" size="small" />}
                          label={
                            <Typography style={{ fontSize: 13 }}>
                              Đại lý kích hoạt
                            </Typography>
                          }
                        />
                        <FormControlLabel
                          value="VGPS kích hoạt"
                          control={<Radio color="primary" size="small" />}
                          label={
                            <Typography style={{ fontSize: 13 }}>
                              VGPS kích hoạt
                            </Typography>
                          }
                        />
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                </Grid>
              ) : (
                <Grid container spacing={2} style={{ marginBottom: 16 }}>
                  <Grid item xs={12}>
                    <FormControl component="fieldset">
                      <Typography
                        variant="subtitle2"
                        style={{ fontSize: 13, color: '#666', marginBottom: 4 }}
                      >
                        Hình thức kích hoạt <span style={{ color: 'red' }}>*</span>
                      </Typography>
                      <RadioGroup
                        row
                        value={form.type}
                        onChange={e => setForm({ ...form, type: e.target.value })}
                      >
                        <FormControlLabel
                          value="Khách  kích hoạt"
                          control={<Radio color="primary" size="small" />}
                          label={
                            <Typography style={{ fontSize: 13 }}>
                              Khách kích hoạt
                            </Typography>
                          }
                        />
                        <FormControlLabel
                          value="VGPS kích hoạt"
                          control={<Radio color="primary" size="small" />}
                          label={
                            <Typography style={{ fontSize: 13 }}>
                              VGPS kích hoạt
                            </Typography>
                          }
                        />
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                </Grid>
              )}

              <Grid container spacing={2} style={{ marginBottom: 16 }}>
                <Grid item xs={12} sm={6}>
                  <FormControl component="fieldset">
                    <Typography
                      variant="subtitle2"
                      style={{ fontSize: 13, color: '#555', marginBottom: 4 }}
                    >
                      Hình thức lắp đặt <span style={{ color: 'red' }}>*</span>
                    </Typography>
                    <RadioGroup
                      row
                      value={form.installationType}
                      onChange={e => {
                        setForm({ ...form, installationType: e.target.value });
                        if (e.target.value !== 'VGPS lắp đặt')
                          setSelectedImages([]);
                      }}
                    >
                      <FormControlLabel
                        value="Khách lắp đặt"
                        control={<Radio color="primary" size="small" />}
                        label={
                          <Typography style={{ fontSize: 13 }}>
                            {tabForm === 0 ? 'Đại lý tự lắp' : 'Khách lẻ lắp'}
                          </Typography>
                        }
                      />
                      <FormControlLabel
                        value="VGPS lắp đặt"
                        control={<Radio color="primary" size="small" />}
                        label={
                          <Typography style={{ fontSize: 13 }}>
                            VGPS lắp đặt
                          </Typography>
                        }
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl component="fieldset">
                    <Typography
                      variant="subtitle2"
                      style={{ fontSize: 13, color: '#555', marginBottom: 4 }}
                    >
                      Phân loại đơn <span style={{ color: 'red' }}>*</span>
                    </Typography>
                    <RadioGroup
                      row
                      value={form.isNewOrder}
                      onChange={e =>
                        setForm({ ...form, isNewOrder: e.target.value })
                      }
                    >
                      <FormControlLabel
                        value="Lắp mới "
                        control={<Radio color="primary" size="small" />}
                        label={
                          <Typography style={{ fontSize: 13 }}>Lắp mới</Typography>
                        }
                      />
                      <FormControlLabel
                        value="Thay cũ lắp mới  lấy mới"
                        control={<Radio color="primary" size="small" />}
                        label={
                          <Typography style={{ fontSize: 13 }}>
                            Thay cũ lắp mới{' '}
                          </Typography>
                        }
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>
              </Grid>

              <Divider style={{ marginBottom: 16 }} />
            </>
          )}

          {form.order_type !== 'PART_SALE' && (
            <>
              <Divider style={{ marginBottom: 16 }} />
              <Typography
                variant="subtitle2"
                style={{ fontWeight: 600, marginBottom: 16, color: '#1a1f36' }}
              >
                Chi tiết thiết bị & Số lượng <span style={{ color: 'red' }}>*</span>
              </Typography>

              {devices.map((device, index) => {
                const is2Cam = (device.type || '').toUpperCase().replace('-', '_').includes('2CAM');
                const effectiveSimType = tabForm === 1 ? 'Sim VGPS' : (device.simType || 'Sim đại lý');

                return (
                  <Box
                    key={index}
                    style={{
                      backgroundColor: '#f8f9fa',
                      border: '1px solid #e9ecef',
                      borderRadius: 8,
                      padding: '12px 16px',
                      marginBottom: 16
                    }}
                  >
                    <Grid
                      container
                      spacing={2}
                      alignItems="center"
                    >
                      <Grid item xs={5}>
                        <FormControl fullWidth variant="outlined" size="small">
                          <InputLabel
                            style={{
                              backgroundColor: 'white',
                              padding: '0 4px',
                              marginLeft: -4
                            }}
                          >
                            Loại thiết bị
                          </InputLabel>
                          <Select
                            value={device.type}
                            disabled={isEditing && Boolean(device.id)}
                            onChange={e =>
                              handleDeviceChange(index, 'type', e.target.value)
                            }
                            label="Loại thiết bị"
                            MenuProps={{
                              getContentAnchorEl: null,
                              anchorOrigin: {
                                vertical: 'bottom',
                                horizontal: 'left'
                              },
                              transformOrigin: {
                                vertical: 'top',
                                horizontal: 'left'
                              },
                              PaperProps: {
                                style: {
                                  maxHeight: 280,
                                  boxShadow: '0 4px 16px rgba(0,0,0,0.15)'
                                }
                              }
                            }}
                          >
                            {listDeviceType?.map(dt => (
                              <MenuItem key={dt.id} value={dt.name}>
                                {dt.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={2}>
                        <FormControl fullWidth variant="outlined" size="small">
                          <InputLabel
                            variant="outlined"
                            shrink
                            style={{
                              backgroundColor: 'white',
                              padding: '0 4px',
                              marginLeft: -4
                            }}
                          >
                            Số lượng
                          </InputLabel>
                          <OutlinedInput
                            label="Số lượng"
                            type="number"
                            inputProps={{ min: 1 }}
                            value={device.quantity}
                            onChange={e =>
                              handleDeviceChange(
                                index,
                                'quantity',
                                Number(e.target.value)
                              )
                            }
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={4}>
                        <FormControl fullWidth variant="outlined" size="small">
                          <InputLabel
                            variant="outlined"
                            shrink
                            style={{
                              backgroundColor: 'white',
                              padding: '0 4px',
                              marginLeft: -4
                            }}
                          >
                            Đơn giá
                          </InputLabel>
                          <OutlinedInput
                            label="Đơn giá"
                            value={formatInputMoney(device.unitPrice || 0)}
                            onChange={e =>
                              handleDeviceChange(
                                index,
                                'unitPrice',
                                parseInputMoney(e.target.value)
                              )
                            }
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={1} style={{ textAlign: 'right' }}>
                        <IconButton
                          size="small"
                          color="secondary"
                          onClick={() => handleRemoveDevice(index)}
                          disabled={devices.length === 1}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Grid>
                    </Grid>

                    <Box display="flex" alignItems="center" flexWrap="wrap" style={{ marginTop: 10, paddingTop: 8, borderTop: '1px dashed #dedede', gap: 12 }}>
                      <Typography variant="subtitle2" style={{ fontSize: 13, color: '#666', fontWeight: 600 }}>
                        Chọn SIM <span style={{ color: 'red' }}>*</span>:
                      </Typography>

                      {tabForm === 0 ? (
                        <RadioGroup
                          row
                          value={effectiveSimType}
                          onChange={e => handleDeviceChange(index, 'simType', e.target.value)}
                        >
                          <FormControlLabel
                            value="Sim đại lý"
                            control={<Radio color="primary" size="small" />}
                            label={<Typography style={{ fontSize: 13 }}>Sim đại lý</Typography>}
                          />
                          <FormControlLabel
                            value="Sim VGPS"
                            control={<Radio color="primary" size="small" />}
                            label={<Typography style={{ fontSize: 13 }}>Sim VGPS</Typography>}
                          />
                        </RadioGroup>
                      ) : (
                        <RadioGroup
                          row
                          value="Sim VGPS"
                          onChange={e => handleDeviceChange(index, 'simType', e.target.value)}
                        >
                          <FormControlLabel
                            value="Sim VGPS"
                            control={<Radio color="primary" size="small" />}
                            label={<Typography style={{ fontSize: 13 }}>Sim VGPS</Typography>}
                          />
                        </RadioGroup>
                      )}

                      {effectiveSimType === 'Sim VGPS' && (
                        <Box display="flex" alignItems="center" style={{ marginLeft: 8 }}>
                          <Typography variant="caption" style={{ color: '#888', marginRight: 8, fontWeight: 500 }}>
                            Loại SIM:
                          </Typography>
                          <RadioGroup
                            row
                            value={device.simNetwork || ''}
                            onChange={e => handleDeviceChange(index, 'simNetwork', e.target.value)}
                          >
                            {is2Cam ? (
                              <>
                                <FormControlLabel
                                  value="Sim ảnh"
                                  control={<Radio color="primary" size="small" />}
                                  label={<Typography style={{ fontSize: 12 }}>Sim ảnh</Typography>}
                                />
                                <FormControlLabel
                                  value="Sim live"
                                  control={<Radio color="primary" size="small" />}
                                  label={<Typography style={{ fontSize: 12 }}>Sim live</Typography>}
                                />
                              </>
                            ) : (
                              <>
                                <FormControlLabel
                                  value="Viettel"
                                  control={<Radio color="primary" size="small" />}
                                  label={<Typography style={{ fontSize: 12 }}>Viettel</Typography>}
                                />
                                <FormControlLabel
                                  value="Vinaphone"
                                  control={<Radio color="primary" size="small" />}
                                  label={<Typography style={{ fontSize: 12 }}>Vinaphone</Typography>}
                                />
                              </>
                            )}
                          </RadioGroup>
                        </Box>
                      )}
                    </Box>
                  </Box>
                );
              })}

              <Button
                startIcon={<AddCircleOutlineIcon />}
                color="primary"
                onClick={handleAddDevice}
                style={{
                  marginBottom: 20,
                  textTransform: 'none',
                  fontWeight: 600
                }}
              >
                Thêm loại thiết bị khác
              </Button>

              <Divider style={{ marginBottom: 16 }} />
            </>
          )}
          {(form.order_type === 'PART_SALE' || form.order_type === 'BOTH') && (
            <>
              <Typography
                variant="subtitle2"
                style={{ fontWeight: 600, marginBottom: 16, color: '#1a1f36' }}
              >
                Chi tiết phụ kiện
              </Typography>

              {orderParts.map((part, index) => {
                return (
                  <Grid
                    container
                    spacing={2}
                    key={index}
                    alignItems="center"
                    style={{ marginBottom: 16 }}
                  >
                    <Grid item xs={5}>
                      <FormControl fullWidth variant="outlined" size="small">
                        <InputLabel
                          style={{
                            backgroundColor: 'white',
                            padding: '0 4px',
                            marginLeft: -4
                          }}
                        >
                          Phụ kiện
                        </InputLabel>
                        <Select
                          value={part.product_id || ''}
                          disabled={isEditing && Boolean(part.id)}
                          onChange={e => {
                            const selectedProd = (productsList || []).find(p => p.id === e.target.value);
                            handlePartSelect(index, selectedProd);
                          }}
                          label="Phụ kiện"
                          renderValue={(selectedId) => {
                            const selectedProd = (productsList || []).find(p => p.id === selectedId);
                            if (!selectedProd) return '';
                            return selectedProd.code ? `[${selectedProd.code}]` : selectedProd.name || '';
                          }}
                          MenuProps={{
                            getContentAnchorEl: null,
                            anchorOrigin: {
                              vertical: 'bottom',
                              horizontal: 'left'
                            },
                            transformOrigin: {
                              vertical: 'top',
                              horizontal: 'left'
                            },
                            PaperProps: {
                              style: {
                                maxHeight: 230,
                                boxShadow: '0 4px 16px rgba(0,0,0,0.15)'
                              }
                            }
                          }}
                        >
                          {(productsList || []).map(p => {
                            const imageUrl = getProductImageUrl(p);
                            const description = p.description || p.name || '---';
                            const displayCode = p.code ? `[${p.code}]` : p.name || '';

                            return (
                              <MenuItem key={p.id} value={p.id} style={{ height: 36, minHeight: 36, padding: 0 }}>
                                <Tooltip
                                  placement="right"
                                  arrow
                                  interactive
                                  PopperProps={{
                                    modifiers: {
                                      preventOverflow: {
                                        enabled: true,
                                        boundariesElement: 'window',
                                        padding: 20
                                      }
                                    }
                                  }}
                                  classes={{ tooltip: classes.customTooltip, arrow: classes.customArrow }}
                                  title={
                                    <Box display="flex" alignItems="flex-start" style={{ padding: '2px 0', gap: 14 }}>
                                      <Box
                                        style={{
                                          width: 90,
                                          height: 90,
                                          minWidth: 90,
                                          borderRadius: 8,
                                          backgroundColor: '#ffffff',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          padding: 6,
                                          boxSizing: 'border-box',
                                          overflow: 'hidden',
                                          flexShrink: 0,
                                          position: 'sticky',
                                          top: 0
                                        }}
                                      >
                                        {imageUrl ? (
                                          <img
                                            src={imageUrl}
                                            alt={p.name || ''}
                                            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                          />
                                        ) : (
                                          <Typography style={{ color: '#999', fontSize: 11 }}>Không có ảnh</Typography>
                                        )}
                                      </Box>
                                      <Box style={{ flex: 1, minWidth: 0 }}>
                                        <Typography style={{ color: '#ff7b92', fontWeight: 700, fontSize: 14, lineHeight: 1.35, marginBottom: 6 }}>
                                          {p.name || ''} {p.code ? `(${p.code})` : ''}
                                        </Typography>
                                        <Typography
                                          style={{
                                            color: '#ffffff',
                                            fontSize: 12,
                                            lineHeight: 1.5,
                                            wordBreak: 'break-word',
                                            whiteSpace: 'pre-line'
                                          }}
                                        >
                                          <b>Mô tả:</b> {description}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  }
                                >
                                  <Box width="100%" height="100%" display="flex" alignItems="center" px={2}>
                                    {displayCode}
                                  </Box>
                                </Tooltip>
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={2}>
                      <FormControl fullWidth variant="outlined" size="small">
                        <InputLabel
                          variant="outlined"
                          shrink
                          style={{
                            backgroundColor: 'white',
                            padding: '0 4px',
                            marginLeft: -4
                          }}
                        >
                          Số lượng
                        </InputLabel>
                        <OutlinedInput
                          label="Số lượng"
                          type="number"
                          inputProps={{ min: 1 }}
                          value={part.quantity}
                          onChange={e =>
                            handlePartChange(index, 'quantity', Number(e.target.value) || 1)
                          }
                        />
                      </FormControl>
                    </Grid>
                    <Grid item xs={4}>
                      <FormControl fullWidth variant="outlined" size="small">
                        <InputLabel
                          variant="outlined"
                          shrink
                          style={{
                            backgroundColor: 'white',
                            padding: '0 4px',
                            marginLeft: -4
                          }}
                        >
                          Đơn giá
                        </InputLabel>
                        <OutlinedInput
                          label="Đơn giá"
                          value={formatInputMoney(part.unit_price || 0)}
                          onChange={e =>
                            handlePartChange(index, 'unit_price', parseInputMoney(e.target.value))
                          }
                        />
                      </FormControl>
                    </Grid>
                    <Grid item xs={1} style={{ textAlign: 'right' }}>
                      <IconButton
                        size="small"
                        color="secondary"
                        onClick={() => handleRemovePart(index)}
                        disabled={orderParts.length === 1}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                );
              })}

              <Button
                startIcon={<AddCircleOutlineIcon />}
                color="primary"
                onClick={handleAddPart}
                style={{
                  marginBottom: 20,
                  textTransform: 'none',
                  fontWeight: 600
                }}
              >
                Thêm phụ kiện khác
              </Button>
            </>
          )}

          <Box
            mt={2}
            mb={2}
            p={2}
            style={{ backgroundColor: '#f9f9f9', borderRadius: 8 }}
          >
            {form.order_type === 'BOTH' ? (
              <>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">Tạm tính thiết bị:</Typography>
                  <Typography variant="body2" style={{ fontWeight: 600 }}>
                    {formatMoney(
                      devices.reduce((acc, d) => acc + d.quantity * d.unitPrice, 0)
                    )}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">Tạm tính phụ kiện:</Typography>
                  <Typography variant="body2" style={{ fontWeight: 600 }}>
                    {formatMoney(
                      orderParts.reduce((acc, p) => acc + (p.quantity || 0) * (p.unit_price || 0), 0)
                    )}
                  </Typography>
                </Box>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={1}
                >
                  <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                    <Typography variant="body2">VAT:</Typography>
                    <TextField
                      size="small"
                      variant="outlined"
                      value={form.vat}
                      onChange={e => {
                        const v = e.target.value.replace(/[^0-9.]/g, '');
                        setForm({ ...form, vat: v === '' ? '' : Number(v) });
                      }}
                      InputProps={{
                        style: { fontSize: 13, height: 28, width: 64 },
                        endAdornment: (
                          <Typography
                            style={{ fontSize: 16, color: '#888', marginLeft: 2 }}
                          >
                            %
                          </Typography>
                        )
                      }}
                    />
                  </Box>
                  <Typography variant="body2" style={{ fontWeight: 600 }}>
                    {formatMoney(
                      (
                        devices.reduce((acc, d) => acc + d.quantity * d.unitPrice, 0) +
                        orderParts.reduce((acc, p) => acc + (p.quantity || 0) * (p.unit_price || 0), 0)
                      ) * ((Number(form.vat) || 0) / 100)
                    )}
                  </Typography>
                </Box>
                <Divider style={{ margin: '8px 0' }} />
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
                    Tổng tiền đơn hàng:
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    style={{ fontWeight: 700, color: '#f44336' }}
                  >
                    {formatMoney(
                      (
                        devices.reduce((acc, d) => acc + d.quantity * d.unitPrice, 0) +
                        orderParts.reduce((acc, p) => acc + (p.quantity || 0) * (p.unit_price || 0), 0)
                      ) * (1 + (Number(form.vat) || 0) / 100)
                    )}
                  </Typography>
                </Box>
              </>
            ) : form.order_type === 'PART_SALE' ? (
              <>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">Tạm tính phụ kiện:</Typography>
                  <Typography variant="body2" style={{ fontWeight: 600 }}>
                    {formatMoney(
                      orderParts.reduce((acc, p) => acc + (p.quantity || 0) * (p.unit_price || 0), 0)
                    )}
                  </Typography>
                </Box>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={1}
                >
                  <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                    <Typography variant="body2">VAT:</Typography>
                    <TextField
                      size="small"
                      variant="outlined"
                      value={form.vat}
                      onChange={e => {
                        const v = e.target.value.replace(/[^0-9.]/g, '');
                        setForm({ ...form, vat: v === '' ? '' : Number(v) });
                      }}
                      InputProps={{
                        style: { fontSize: 13, height: 28, width: 64 },
                        endAdornment: (
                          <Typography
                            style={{ fontSize: 16, color: '#888', marginLeft: 2 }}
                          >
                            %
                          </Typography>
                        )
                      }}
                    />
                  </Box>
                  <Typography variant="body2" style={{ fontWeight: 600 }}>
                    {formatMoney(
                      orderParts.reduce((acc, p) => acc + (p.quantity || 0) * (p.unit_price || 0), 0) *
                      ((Number(form.vat) || 0) / 100)
                    )}
                  </Typography>
                </Box>
                <Divider style={{ margin: '8px 0' }} />
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
                    Tổng tiền phụ kiện:
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    style={{ fontWeight: 700, color: '#f44336' }}
                  >
                    {formatMoney(
                      orderParts.reduce((acc, p) => acc + (p.quantity || 0) * (p.unit_price || 0), 0) *
                      (1 + (Number(form.vat) || 0) / 100)
                    )}
                  </Typography>
                </Box>
              </>
            ) : (
              <>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">Tạm tính thiết bị:</Typography>
                  <Typography variant="body2" style={{ fontWeight: 600 }}>
                    {formatMoney(
                      devices.reduce((acc, d) => acc + d.quantity * d.unitPrice, 0)
                    )}
                  </Typography>
                </Box>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={1}
                >
                  <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                    <Typography variant="body2">VAT:</Typography>
                    <TextField
                      size="small"
                      variant="outlined"
                      value={form.vat}
                      onChange={e => {
                        const v = e.target.value.replace(/[^0-9.]/g, '');
                        setForm({ ...form, vat: v === '' ? '' : Number(v) });
                      }}
                      InputProps={{
                        style: { fontSize: 13, height: 28, width: 64 },
                        endAdornment: (
                          <Typography
                            style={{ fontSize: 16, color: '#888', marginLeft: 2 }}
                          >
                            %
                          </Typography>
                        )
                      }}
                    />
                  </Box>
                  <Typography variant="body2" style={{ fontWeight: 600 }}>
                    {formatMoney(
                      devices.reduce((acc, d) => acc + d.quantity * d.unitPrice, 0) *
                      ((Number(form.vat) || 0) / 100)
                    )}
                  </Typography>
                </Box>
                <Divider style={{ margin: '8px 0' }} />
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
                    Tiền thiết bị:
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    style={{ fontWeight: 700, color: '#f44336' }}
                  >
                    {formatMoney(
                      devices.reduce((acc, d) => acc + d.quantity * d.unitPrice, 0) *
                      (1 + (Number(form.vat) || 0) / 100)
                    )}
                  </Typography>
                </Box>
              </>
            )}
          </Box>
          <Divider style={{ marginBottom: 16 }} />



          <FormControl fullWidth variant="outlined" size="small">
            <InputLabel
              variant="outlined"
              shrink
              style={{
                backgroundColor: 'white',
                padding: '0 4px',
                marginLeft: -4
              }}
            >
              Ghi chú đơn hàng
            </InputLabel>
            <OutlinedInput
              multiline
              rows={2}
              label="Ghi chú đơn hàng"
              name="note"
              value={form.note}
              onChange={e => setForm({ ...form, note: e.target.value })}
            />
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions style={{ padding: 16 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          color="primary"
          style={{ fontWeight: 600 }}
        >
          Hủy
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleCreateOrder}
          disabled={submitting}
          style={{ fontWeight: 600 }}
        >
          {submitting ? 'Đang xử lý...' : (isEditing ? 'Cập nhật' : 'Hoàn Thành')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OrderDialog;
